import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import OrganizationContext from '@totem/components/orgManagement/orgContext';
import AddressContext from '@totem/components/orgManagement/orgDetails/buildings/options/addressContext';
import { NetSuiteAddressRecord } from '@totem/components/orgManagement/orgDetails/netSuiteKeys/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { API_V1 } from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

const AddressesContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { actionItem } = useContext(OrganizationContext);
  const [data, setData] = useState<NetSuiteAddressRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  const customers = useMemo(() => {
    if (isNotNull(actionItem) && isNotNull(actionItem.keys)) {
      const customerKeys = actionItem.keys
        .filter((chk) => chk.system === 'NetSuite' && chk.instance === 'IB')
        .map((item) => item.key);
      return customerKeys;
    }

    return [];
  }, [actionItem]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }

    setRefreshData(false);

    if (searchTerm === '') {
      setData([]);
      return;
    }

    const payload = {
      customers,
      page,
      pageSize,
      searchTerm,
    };

    fetch(`${API_V1}/addresses/search`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result: NetSuiteAddressRecord[]) => {
        setData(result);
      });
  }, [refreshData]);

  const handleSetSearchTerm = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);

    setPage(0);

    setRefreshData(true);
  };

  const handleSetPageSize = (newPageSize: number) => {
    setPageSize(newPageSize);

    setPage(0);

    setRefreshData(true);
  };

  return (
    <AddressContext.Provider
      value={{
        searchTerm,
        page,
        pageSize,
        netSuiteAddresses: data,
        onRefresh: () => setRefreshData(true),
        setSearchTerm: handleSetSearchTerm,
        setPageSize: handleSetPageSize,
        setPage: (newPage: number) => setPage(newPage),
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};

export default AddressesContainer;
